import React, { FunctionComponent } from 'react';
import { SbBlokData, storyblokEditable } from '@storyblok/react';
import classnames from 'classnames';

import { ComparisonTable, Heading, P } from '@savebetter/components';

import { PageSectionBlok, PageSectionProps } from './PageSection';
import { PageSectionHeader } from './PageSectionHeader';
import { RichText, RichTextProps } from './RichText';

interface ContentTableHeaders extends SbBlokData {
  imageUrl: string;
  imageAlt: string;
  imageLoading: 'lazy' | 'eager' | undefined;
  headerTitle: string;
  headerIntro: string;
}

interface ContentTableCategoryText extends SbBlokData {
  text: RichTextProps;
}

interface ContentTableCategories extends SbBlokData {
  rowTitle: string;
  descriptions: ContentTableCategoryText[];
}

interface ContentTableTextParagraph extends SbBlokData {
  title: string;
  description: string;
}

interface Blok extends PageSectionBlok {
  contentTitle?: string;
  headers: ContentTableHeaders[];
  categories: ContentTableCategories[];
  textParagraph?: ContentTableTextParagraph[];
}

interface ContentTableV2Props extends PageSectionProps {
  blok: Blok;
}

const wrapperClassNames = (
  hasBackground = true,
  hasBorder = false,
  className = '',
  hasPaddingBottom = false,
  hasPaddingTop = false
): string =>
  classnames(
    {
      'bg-black-20': hasBackground,
      'sb-section-has-border-bottom': hasBorder,
      'pb-0': !hasPaddingBottom,
      'pt-4': !hasPaddingTop,
      'pb-26 sm:pb-8 md:pb-26': hasPaddingBottom,
      'pt-30 sm:pt-8 md:pt-30': hasPaddingTop
    },
    className
  );

export const ContentTableV2: FunctionComponent<React.PropsWithChildren<ContentTableV2Props>> = ({
  blok,
  sectionClassName
}) => {
  const {
    hasBackground,
    hasBorder,
    contentTitle,
    headers,
    categories,
    textParagraph,
    hasPaddingBottom,
    hasPaddingTop
  } = blok;

  const contentCategories = categories.map(category => ({
    rowTitle: category.rowTitle,
    descriptions: category.descriptions.map((description, index) => <RichText key={index} content={description.text} />)
  }));

  return (
    <div
      className={wrapperClassNames(hasBackground, hasBorder, sectionClassName, hasPaddingBottom, hasPaddingTop)}
      {...storyblokEditable(blok)}
    >
      {contentTitle && <PageSectionHeader className="mx-5">{contentTitle}</PageSectionHeader>}
      <ComparisonTable headers={headers} categories={contentCategories} />
      {textParagraph && textParagraph[0] && (
        <div className="w-full">
          <div className="lg:flex lg:justify-center mx-auto max-w-252">
            <Heading
              className="lg:w-3/4 text-center lg:pt-16 lg:pb-6 md:px-5 md:pt-8 sm:pt-8 sm:px-5"
              level="4"
              kind="1"
            >
              {textParagraph[0]?.title}
            </Heading>
          </div>
          <P className="flex max-w-252 mx-auto md:p-5 sm:p-4">{textParagraph[0]?.description}</P>
        </div>
      )}
    </div>
  );
};
